import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import api from "@/lib/api";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";

const DOCS = [
  { slug: "privacy", label: "Privacy & Data Usage" },
  { slug: "terms", label: "Terms of Use" },
  { slug: "data-processing", label: "Data Processing Summary" },
];

// The source content marks emphasis with **double asterisks**.
const Rich = ({ text }) => (
  <>
    {String(text).split("**").map((part, i) =>
      i % 2 === 1 ? <strong key={i} className="font-semibold text-[color:var(--marketing-text)]">{part}</strong> : part
    )}
  </>
);

const Table = ({ table }) => (
  <div className="mt-4 overflow-x-auto">
    <table className="w-full min-w-[640px] border-collapse text-left text-xs">
      <thead>
        <tr>
          {table.columns.map((c) => (
            <th key={c} className="border border-[color:var(--marketing-border)] bg-slate-50 p-2.5 font-semibold">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} className="border border-[color:var(--marketing-border)] p-2.5 align-top text-[color:var(--marketing-text-muted)]">
                <Rich text={cell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Legal() {
  const { slug = "privacy" } = useParams();
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    setDoc(null);
    api.get(`/public/legal/${slug}`).then(({ data }) => setDoc(data)).catch(() => setDoc(false));
    window.scrollTo(0, 0);
  }, [slug]);

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/legal/${slug}.pdf`;

  return (
    <div className="marketing-root min-h-screen bg-[color:var(--marketing-bg)] text-[color:var(--marketing-text)]">
      <MarketingNav />
      <main className="mx-auto max-w-7xl px-6 py-16 lg:px-10" data-testid={`legal-page-${slug}`}>
        {doc === false ? (
          <p className="text-sm text-[color:var(--marketing-text-muted)]">That document could not be found.</p>
        ) : !doc ? (
          <p className="text-sm text-[color:var(--marketing-text-muted)]">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <p className="overline-label">Legal</p>
              <nav className="mt-3 space-y-1.5">
                {DOCS.map((d) => (
                  <Link
                    key={d.slug}
                    to={`/legal/${d.slug}`}
                    data-testid={`legal-nav-${d.slug}`}
                    className={`block border-l-2 py-1 pl-3 text-sm transition-colors ${
                      d.slug === slug
                        ? "border-[color:var(--marketing-primary)] font-semibold text-[color:var(--marketing-primary)]"
                        : "border-transparent text-[color:var(--marketing-text-muted)] hover:text-[color:var(--marketing-text)]"
                    }`}
                  >
                    {d.label}
                  </Link>
                ))}
              </nav>
              <a
                href={pdfUrl}
                data-testid="legal-pdf-btn"
                className="mt-6 inline-flex items-center gap-1.5 rounded-sm border-2 border-slate-200 px-3 py-2 text-xs font-semibold transition-colors hover:border-slate-900"
              >
                <Download size={13} strokeWidth={1.5} /> Download PDF
              </a>
              <p className="mt-4 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
                <FileText size={12} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                Version {doc.version} · Effective {doc.effective}
              </p>
            </aside>

            <article className="max-w-3xl">
              <h1 className="font-marketing-display text-3xl font-black tracking-tight sm:text-4xl">{doc.title}</h1>
              <p className="mt-4 text-base text-[color:var(--marketing-text-muted)]">{doc.subtitle}</p>
              <p className="mt-4 border-l-2 border-[color:var(--marketing-primary)] pl-4 text-sm text-[color:var(--marketing-text-muted)]">
                {doc.entity.company} · Company number {doc.entity.company_number} · {doc.entity.address}
              </p>

              <div className="mt-10 space-y-10">
                {doc.sections.map((sec) => (
                  <section key={sec.heading} data-testid={`legal-section-${sec.heading.slice(0, 12)}`}>
                    <h2 className="font-marketing-display text-lg font-bold tracking-tight">{sec.heading}</h2>
                    {(sec.paras || []).map((p, i) => (
                      <p key={i} className="mt-3 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">
                        <Rich text={p} />
                      </p>
                    ))}
                    {(sec.bullets || []).length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {sec.bullets.map((b, i) => (
                          <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[color:var(--marketing-primary)]" />
                            <span><Rich text={b} /></span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {sec.table && <Table table={sec.table} />}
                  </section>
                ))}
              </div>
            </article>
          </div>
        )}
      </main>
      <MarketingFooter />
    </div>
  );
}
